// src/pages/FeatureDetailPage.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap, Send } from "lucide-react";
import { useAppContext } from "../../context/AppContext";

const FeatureDetailPage = ({
  icon,
  title,
  description,
  imageSrc,
  benefits,
}) => {
  const { setShowLoginModal } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-primary-500/30 selection:text-white">
      <div className="container mx-auto px-6 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest"
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
        </motion.div>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left Column: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
                {icon}
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                {title}
              </h1>
            </div>

            <p className="mt-4 text-lg md:text-xl text-gray-300 font-light leading-relaxed">
              {description}
            </p>

            <div className="mt-12 space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <Zap size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-200">{benefit}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="mt-12"
            >
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-white text-black font-bold text-lg px-12 py-4 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Send size={18} /> Get Started
              </button>
            </motion.div>
          </motion.div>

          {/* Right Column: Image */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <img
              src={imageSrc}
              alt={`${title} illustration`}
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetailPage;
